<template>
  <div class="space-scene-viewer">
    <div ref="container" class="scene-container"></div>
    <transition name="loading-fade">
      <div v-show="sceneLoading" class="loading-screen">
        <canvas
          ref="starfieldCanvas"
          class="loading-starfield"
          aria-hidden="true"
        ></canvas>
        <div class="loading-content">
          <div class="loading-brand">
            <div class="loading-halo loading-halo-1"></div>
            <div class="loading-halo loading-halo-2"></div>
            <div class="loading-halo loading-halo-3"></div>
            <div class="loading-logo">Universe</div>
          </div>
          <p class="loading-text">进入世界中</p>
          <p class="loading-sub">Loading world...</p>
        </div>
      </div>
    </transition>
    <div class="viewer-controls">
      <span class="control-hint">WASD 移动 · Q/E 上下</span>
      <button class="control-btn" @click="resetCamera">重置视角</button>
    </div>
    <div
      v-if="selectedHotspot"
      class="hotspot-modal"
      @click.self="selectedHotspot = null"
    >
      <div class="hotspot-content">
        <button class="close-btn" @click="selectedHotspot = null">×</button>
        <h3>{{ selectedHotspot.title || "详情" }}</h3>
        <p v-if="selectedHotspot.description" class="desc">
          {{ selectedHotspot.description }}
        </p>
        <template
          v-if="
            selectedHotspot.type === 'image' && selectedHotspot.content?.url
          "
        >
          <img
            :src="resolveImageUrl(selectedHotspot.content.url)"
            alt=""
            class="content-img"
          />
        </template>
        <template
          v-else-if="
            selectedHotspot.type === 'video' && selectedHotspot.content?.url
          "
        >
          <video
            :src="resolveImageUrl(selectedHotspot.content.url)"
            controls
            class="content-video"
          />
        </template>
        <template v-else-if="selectedHotspot.type === 'chain_asset'">
          <p class="chain-info">
            链: {{ selectedHotspot.content?.chain || "-" }}<br />
            合约: {{ selectedHotspot.content?.contractAddress || "-" }}<br />
            Token ID: {{ selectedHotspot.content?.tokenId || "-" }}
          </p>
        </template>
      </div>
    </div>
    <div v-if="loadError" class="load-error">{{ loadError }}</div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick } from "vue";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

/* eslint-disable-next-line no-undef */
const props = defineProps({
  sceneModelUrl: { type: String, default: "" },
  hotspots: { type: Array, default: () => [] },
  /** 空间是否已发布：已发布时仅显示已设置资源的交互点 */
  published: { type: Boolean, default: false },
  /** 进入时的固定视角 { position: {x,y,z}, target: {x,y,z} } */
  initialCamera: { type: Object, default: null },
  /** WASD 移动速度 1–30（管理台可配置） */
  moveSpeed: { type: Number, default: 8 },
});

const container = ref(null);
const starfieldCanvas = ref(null);
const selectedHotspot = ref(null);
const loadError = ref("");
/** 场景模型加载中，用于进场过渡 */
const sceneLoading = ref(true);

/** 进场页粒子星空：仅在 sceneLoading 时运行 */
let starfieldRaf = null;
let starfieldParticles = [];
const initStarfield = () => {
  const canvas = starfieldCanvas.value;
  if (!canvas) return;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  canvas.width = w * dpr;
  canvas.height = h * dpr;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.scale(dpr, dpr);
  const count = 260;
  starfieldParticles = [];
  for (let i = 0; i < count; i++) {
    starfieldParticles.push({
      x: Math.random() * w,
      y: Math.random() * h,
      r: Math.random() * 1.2 + 0.3,
      speed: Math.random() * 0.15 + 0.05,
      opacity: Math.random() * 0.5 + 0.2,
      phase: Math.random() * Math.PI * 2,
    });
  }
};
let starfieldLastDraw = 0;
const STARFIELD_FPS = 30;
const drawStarfield = () => {
  if (!sceneLoading.value || !starfieldCanvas.value) return;
  const now = performance.now();
  if (now - starfieldLastDraw < 1000 / STARFIELD_FPS) {
    starfieldRaf = requestAnimationFrame(drawStarfield);
    return;
  }
  starfieldLastDraw = now;
  const canvas = starfieldCanvas.value;
  const h = canvas.clientHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.scale(dpr, dpr);
  const t = Date.now() * 0.001;
  starfieldParticles.forEach((p) => {
    const yy = (p.y - p.speed * 40 * t) % h;
    const y = yy < 0 ? yy + h : yy;
    const twinkle = 0.5 + 0.5 * Math.sin(t * 2 + p.phase);
    ctx.beginPath();
    ctx.arc(p.x, y, p.r, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(255,255,255,${p.opacity * twinkle})`;
    ctx.fill();
  });
  starfieldRaf = requestAnimationFrame(drawStarfield);
};
const startStarfield = () => {
  initStarfield();
  drawStarfield();
};
const stopStarfield = () => {
  if (starfieldRaf) {
    cancelAnimationFrame(starfieldRaf);
    starfieldRaf = null;
  }
};
const onResizeStarfield = () => {
  if (sceneLoading.value && starfieldCanvas.value) initStarfield();
};

let scene, camera, renderer, controls;
let animationId;
let hotspotMeshes = [];
let modelGroup = null;
const keysPressed = {
  w: false,
  a: false,
  s: false,
  d: false,
  q: false,
  e: false,
};
let clock = null;
const forward = new THREE.Vector3();
const right = new THREE.Vector3();
const moveDelta = new THREE.Vector3();
const upVec = new THREE.Vector3(0, 1, 0);

const initScene = () => {
  if (!container.value) return;
  scene = new THREE.Scene();
  scene.background = new THREE.Color(0x87ceeb);

  camera = new THREE.PerspectiveCamera(
    60,
    container.value.clientWidth / container.value.clientHeight,
    0.1,
    1000
  );
  if (props.initialCamera?.position) {
    camera.position.set(
      props.initialCamera.position.x,
      props.initialCamera.position.y,
      props.initialCamera.position.z
    );
  } else {
    camera.position.set(5, 3, 8);
  }
  if (props.initialCamera?.target) {
    camera.lookAt(
      props.initialCamera.target.x,
      props.initialCamera.target.y,
      props.initialCamera.target.z
    );
  } else {
    camera.lookAt(0, 0, 0);
  }

  renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setSize(container.value.clientWidth, container.value.clientHeight);
  if (renderer.outputColorSpace !== undefined) {
    renderer.outputColorSpace = THREE.SRGBColorSpace;
  }
  container.value.appendChild(renderer.domElement);

  const ambient = new THREE.AmbientLight(0xffffff, 0.6);
  scene.add(ambient);
  const dir = new THREE.DirectionalLight(0xffffff, 0.8);
  dir.position.set(10, 10, 5);
  scene.add(dir);

  controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.05;
  if (props.initialCamera?.target) {
    controls.target.set(
      props.initialCamera.target.x,
      props.initialCamera.target.y,
      props.initialCamera.target.z
    );
  }

  clock = new THREE.Clock();
  pageVisible = true;
  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("keyup", onKeyUp);

  if (props.sceneModelUrl) {
    loadModel(props.sceneModelUrl);
  } else {
    sceneLoading.value = false;
  }
  addHotspotMeshes(props.hotspots);

  window.addEventListener("resize", onResize);
  renderer.domElement.addEventListener("click", onRaycast);
  animate();
};

function onKeyDown(e) {
  const k = e.key.toLowerCase();
  if (Object.prototype.hasOwnProperty.call(keysPressed, k)) {
    keysPressed[k] = true;
    e.preventDefault();
  }
}

function onKeyUp(e) {
  const k = e.key.toLowerCase();
  if (Object.prototype.hasOwnProperty.call(keysPressed, k)) {
    keysPressed[k] = false;
    e.preventDefault();
  }
}

function updateWASDMovement(delta) {
  if (!camera || !controls) return;
  camera.getWorldDirection(forward);
  forward.y = 0;
  forward.normalize();
  right.crossVectors(forward, upVec);
  right.normalize();
  moveDelta.set(0, 0, 0);
  if (keysPressed.w) moveDelta.add(forward);
  if (keysPressed.s) moveDelta.sub(forward);
  if (keysPressed.a) moveDelta.sub(right);
  if (keysPressed.d) moveDelta.add(right);
  if (keysPressed.e) moveDelta.y += 1;
  if (keysPressed.q) moveDelta.y -= 1;
  if (moveDelta.x === 0 && moveDelta.y === 0 && moveDelta.z === 0) return;
  moveDelta.normalize();
  const speed = Math.max(1, Math.min(30, props.moveSpeed ?? 8));
  const v = delta * speed;
  const vy = delta * Math.max(1, speed * 0.5);
  camera.position.x += moveDelta.x * v;
  camera.position.z += moveDelta.z * v;
  camera.position.y += moveDelta.y * vy;
  controls.target.x += moveDelta.x * v;
  controls.target.z += moveDelta.z * v;
  controls.target.y += moveDelta.y * vy;
}

function resolveModelUrl(url) {
  if (!url || typeof url !== "string") return "";
  let u = url.trim();
  if (
    u.startsWith("http://") ||
    u.startsWith("https://") ||
    u.startsWith("data:")
  )
    return u;
  // 本地文件路径（如 D:/project/public/models/xxx.gltf）转为 Web 路径 /models/xxx.gltf
  const isFilePath =
    /^[a-zA-Z]:[/\\]/.test(u) ||
    (u.startsWith("/") === false &&
      /[/\\]public[/\\]/.test(u.replace(/\\/g, "/")));
  if (isFilePath) {
    const normalized = u.replace(/\\/g, "/");
    const publicIndex = normalized.indexOf("/public/");
    if (publicIndex !== -1) {
      u = "/" + normalized.slice(publicIndex + "/public/".length);
    } else if (/[/\\]models[/\\]/.test(normalized)) {
      const modelsMatch = normalized.match(/[/]models[/](.+)$/);
      u = modelsMatch
        ? "/models/" + modelsMatch[1]
        : "/models/" + normalized.split(/[/\\]/).pop();
    }
  }
  const origin = window.location.origin || "";
  const base = (typeof process !== "undefined" && process.env?.BASE_URL) || "/";
  if (u.startsWith("/")) return origin + u;
  return origin + base.replace(/\/$/, "") + (u.startsWith("/") ? u : "/" + u);
}

const loadModel = (url) => {
  if (!url || !scene) {
    sceneLoading.value = false;
    return;
  }
  sceneLoading.value = true;
  loadError.value = "";
  const fullUrl = resolveModelUrl(url);
  const loader = new GLTFLoader();
  const baseUrl = fullUrl.substring(0, fullUrl.lastIndexOf("/") + 1);
  if (baseUrl) loader.setResourcePath(baseUrl);
  loader.load(
    fullUrl,
    (gltf) => {
      if (modelGroup) scene.remove(modelGroup);
      modelGroup = gltf.scene;
      modelGroup.scale.setScalar(2.5);
      scene.add(modelGroup);
      sceneLoading.value = false;
    },
    undefined,
    (err) => {
      loadError.value = "模型加载失败";
      console.error(err);
      sceneLoading.value = false;
    }
  );
};

/** 交互点是否已设置资源（已发布时未设置资源的不显示） */
function hasHotspotContent(h) {
  if (!h || !h.type) return false;
  if (h.type === "image" || h.type === "video")
    return !!(h.content && h.content.url);
  if (h.type === "chain_asset")
    return !!(
      h.content &&
      (h.content.chain || h.content.contractAddress || h.content.tokenId)
    );
  return false;
}

const textureLoader = new THREE.TextureLoader();

const OSS_ORIGIN = "https://ruixin-ai.oss-cn-hangzhou.aliyuncs.com";
/** 开发环境下可选走本地代理绕过 CORS（需在 .env 设 VUE_APP_USE_OSS_PROXY=true）；否则直接用 OSS 地址，需在 OSS 控制台配置 CORS */
function resolveImageUrl(url) {
  if (!url || typeof url !== "string") return url;
  const useProxy =
    process.env.NODE_ENV === "development" &&
    process.env.VUE_APP_USE_OSS_PROXY === "true";
  if (useProxy && url.startsWith(OSS_ORIGIN + "/")) {
    return "/oss-img" + url.slice(OSS_ORIGIN.length);
  }
  return url;
}

const addHotspotMeshes = (list) => {
  hotspotMeshes.forEach((m) => {
    if (m.userData?.hotspot && scene) scene.remove(m);
  });
  hotspotMeshes = [];
  if (!list || !Array.isArray(list) || !scene) return;

  const showList = props.published
    ? list.filter((h) => hasHotspotContent(h))
    : list;

  const geometry = new THREE.PlaneGeometry(1.5, 1);

  showList.forEach((h) => {
    const isImage = h.type === "image" && h.content && h.content.url;
    const material = new THREE.MeshBasicMaterial({
      color: isImage ? 0xffffff : 0x4488ff,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: isImage ? 1 : 0.8,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(
      h.position?.x ?? 0,
      h.position?.y ?? 0,
      h.position?.z ?? 0
    );
    mesh.rotation.set(
      h.rotation?.x ?? 0,
      h.rotation?.y ?? 0,
      h.rotation?.z ?? 0
    );
    const scale = typeof h.scale === "number" && h.scale > 0 ? h.scale : 1;
    mesh.scale.set(scale, scale, 1);
    mesh.userData = { hotspot: h, index: list.indexOf(h) };
    scene.add(mesh);
    hotspotMeshes.push(mesh);

    if (isImage && h.content.url) {
      textureLoader.load(
        resolveImageUrl(h.content.url),
        (tex) => {
          if (mesh.material) {
            mesh.material.map = tex;
            if (tex.colorSpace !== undefined)
              tex.colorSpace = THREE.SRGBColorSpace;
          }
          if (tex.image && tex.image.width && tex.image.height) {
            const aspect = tex.image.width / tex.image.height;
            mesh.scale.set((aspect / 1.5) * scale, 1 * scale, 1);
          }
        },
        undefined,
        () => {
          if (mesh.material) mesh.material.color.setHex(0x4488ff);
        }
      );
    }
  });
};

const onRaycast = (e) => {
  if (!camera || !scene || !renderer) return;
  const rect = renderer.domElement.getBoundingClientRect();
  const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
  const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
  const mouse = new THREE.Vector2(x, y);
  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(mouse, camera);
  const intersects = raycaster.intersectObjects(hotspotMeshes);
  if (intersects.length > 0 && intersects[0].object.userData.hotspot) {
    selectedHotspot.value = intersects[0].object.userData.hotspot;
  }
};

const resetCamera = () => {
  if (props.initialCamera?.position && camera) {
    camera.position.set(
      props.initialCamera.position.x,
      props.initialCamera.position.y,
      props.initialCamera.position.z
    );
    if (props.initialCamera.target) {
      camera.lookAt(
        props.initialCamera.target.x,
        props.initialCamera.target.y,
        props.initialCamera.target.z
      );
      if (controls)
        controls.target.set(
          props.initialCamera.target.x,
          props.initialCamera.target.y,
          props.initialCamera.target.z
        );
    }
  } else if (camera) {
    camera.position.set(5, 3, 8);
    camera.lookAt(0, 0, 0);
    if (controls) controls.target.set(0, 0, 0);
  }
};

const onResize = () => {
  if (!container.value || !camera || !renderer) return;
  const w = container.value.clientWidth;
  const h = container.value.clientHeight;
  camera.aspect = w / h;
  camera.updateProjectionMatrix();
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setSize(w, h);
};

let pageVisible = true;
const animate = () => {
  animationId = requestAnimationFrame(animate);
  if (!pageVisible) return;
  const delta = clock ? clock.getDelta() : 0;
  updateWASDMovement(delta);
  if (controls) controls.update();
  if (renderer && scene && camera) renderer.render(scene, camera);
};
const onVisibilityChange = () => {
  pageVisible = document.visibilityState === "visible";
};

function disposeSceneObject(obj) {
  if (!obj) return;
  if (obj.geometry) obj.geometry.dispose();
  if (obj.material) {
    if (Array.isArray(obj.material)) {
      obj.material.forEach((m) => {
        if (m.map) m.map.dispose();
        m.dispose();
      });
    } else {
      if (obj.material.map) obj.material.map.dispose();
      obj.material.dispose();
    }
  }
  if (obj.children) obj.children.forEach(disposeSceneObject);
}
const dispose = () => {
  document.removeEventListener("visibilitychange", onVisibilityChange);
  window.removeEventListener("resize", onResize);
  window.removeEventListener("keydown", onKeyDown);
  window.removeEventListener("keyup", onKeyUp);
  if (renderer?.domElement)
    renderer.domElement.removeEventListener("click", onRaycast);
  if (animationId) cancelAnimationFrame(animationId);
  if (scene) {
    scene.traverse(disposeSceneObject);
  }
  if (controls) controls.dispose();
  hotspotMeshes = [];
  if (
    renderer &&
    container.value &&
    renderer.domElement.parentNode === container.value
  ) {
    container.value.removeChild(renderer.domElement);
  }
  renderer?.dispose();
};

watch(
  () => props.sceneModelUrl,
  (url) => {
    if (scene && url) loadModel(url);
  }
);
watch(
  () => [props.hotspots, props.published],
  () => {
    addHotspotMeshes(props.hotspots || []);
  },
  { deep: true }
);

watch(
  () => sceneLoading.value,
  (v) => {
    if (v) nextTick(startStarfield);
    else stopStarfield();
  }
);
onMounted(() => {
  initScene();
  if (sceneLoading.value) nextTick(startStarfield);
  window.addEventListener("resize", onResizeStarfield);
});
onUnmounted(() => {
  stopStarfield();
  window.removeEventListener("resize", onResizeStarfield);
  dispose();
});
</script>

<style scoped>
.space-scene-viewer {
  position: relative;
  width: 100%;
  height: 100vh;
  min-height: 400px;
}
.scene-container {
  width: 100%;
  height: 100%;
}

.loading-screen {
  position: absolute;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #060608;
}
.loading-starfield {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  z-index: 0;
}
.loading-content {
  position: relative;
  z-index: 1;
  text-align: center;
}
.loading-brand {
  position: relative;
  width: 140px;
  height: 140px;
  margin: 0 auto 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.loading-halo {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.18);
  animation: loading-pulse 2.2s ease-in-out infinite;
}
.loading-halo-1 {
  animation-delay: 0s;
}
.loading-halo-2 {
  inset: -12px;
  border-color: rgba(255, 255, 255, 0.1);
  animation-delay: 0.3s;
}
.loading-halo-3 {
  inset: -24px;
  border-color: rgba(255, 255, 255, 0.06);
  animation-delay: 0.6s;
}
.loading-logo {
  position: relative;
  z-index: 1;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: 0.2em;
  color: rgba(255, 255, 255, 0.95);
  text-shadow: 0 0 24px rgba(255, 255, 255, 0.25);
  animation: loading-breathe 2.2s ease-in-out infinite;
}
.loading-text {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  letter-spacing: 0.12em;
}
.loading-sub {
  margin: 6px 0 0;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  letter-spacing: 0.08em;
}
@keyframes loading-pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.08);
    opacity: 1;
  }
}
@keyframes loading-breathe {
  0%,
  100% {
    opacity: 0.88;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.02);
  }
}
.loading-fade-enter-active,
.loading-fade-leave-active {
  transition: opacity 0.5s ease;
}
.loading-fade-enter-from,
.loading-fade-leave-to {
  opacity: 0;
}
.loading-fade-enter-to,
.loading-fade-leave-from {
  opacity: 1;
}

.viewer-controls {
  position: absolute;
  bottom: 16px;
  left: 16px;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 12px;
}
.control-hint {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}
.control-btn {
  padding: 8px 16px;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}
.hotspot-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.hotspot-content {
  background: var(--ux-card-bg);
  padding: 24px;
  border-radius: 12px;
  max-width: 90vw;
  max-height: 90vh;
  overflow: auto;
  position: relative;
}
.close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  font-size: 24px;
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
}
.content-img {
  max-width: 100%;
  max-height: 70vh;
  display: block;
}
.content-video {
  max-width: 100%;
  max-height: 70vh;
}
.chain-info {
  font-size: 14px;
  color: #333;
  line-height: 1.6;
}
.load-error {
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(200, 0, 0, 0.9);
  color: #fff;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
}
</style>
